import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { IoClose } from 'react-icons/io5';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { SERVICE_CATEGORIES, PARTNER_SIGNUP_CATEGORIES } from '../../../config/ieumConfig';
import Pagination from '../../../components/common/Pagination';
import {
  DesktopListOnly, MobileCardActions, MobileCardField, MobileCardGrid,
  MobileCardHeader, MobileCardHeading, MobileCardLabel, MobileCardList,
  MobileCardStatus, MobileCardSubtitle, MobileCardTitle, MobileCardValue,
  MobileListCard, MobileListState,
} from '../../../components/common/AdminMobileList';
import { getAllPartners, approveUser, rejectUser } from '../../../services/memberService';
import { signup } from '../../../services/authService';

const STATUS_LABEL = {
  active: '활성', pending: '승인 대기', suspended: '정지', inactive: '비활성',
};

const STATUS_COLOR = {
  active: '#22C55E', pending: '#F59E0B', inactive: '#9CA3AF', suspended: '#EF4444',
};

const PER_PAGE = 20;

// ───── 스타일 ─────
const Wrap = styled.div``;

const TopRow = styled.div`
  display: flex; justify-content: space-between; align-items: flex-end;
  margin-bottom: 20px;
  gap: 12px; flex-wrap: wrap;
  @media (max-width: 768px) { align-items: stretch; margin-bottom: 14px; }
`;

const TitleBox = styled.div``;
const Title = styled.h1` font-size: 18px; font-weight: 600; `;
const CountText = styled.span`
  font-size: 13px; color: ${({ theme }) => theme.colors.textSecondary};
  margin-left: 8px;
`;

const ActionRow = styled.div`
  display: flex; gap: 8px;
  @media (max-width: 768px) {
    display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); width: 100%;
    > button:last-child { grid-column: 1 / -1; }
  }
`;

const Btn = styled.button`
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px; font-weight: 500;
  border: 1px solid ${({ theme, $variant }) =>
    $variant === 'primary' ? theme.colors.primary : theme.colors.border};
  background: ${({ theme, $variant }) =>
    $variant === 'primary' ? theme.colors.primary : '#fff'};
  color: ${({ $variant }) => ($variant === 'primary' ? '#fff' : '#374151')};
  cursor: pointer;
  white-space: nowrap;
  &:hover { opacity: 0.92; }
  @media (max-width: 768px) { min-height: 44px; padding: 9px 10px; }
`;

const FilterBar = styled.div`
  display: flex; gap: 10px; margin-bottom: 16px;
  flex-wrap: wrap;
  @media (max-width: 768px) { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
`;

const SearchInput = styled.input`
  padding: 8px 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: 13px;
  width: 260px;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
  @media (max-width: 768px) { grid-column: 1 / -1; width: 100%; min-height: 44px; }
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: 13px;
  background: #fff;
  cursor: pointer;
  @media (max-width: 768px) { width: 100%; min-width: 0; min-height: 44px; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
  overflow: hidden;

  thead tr { background: #F9FAFB; }
  th, td {
    padding: 12px 14px;
    text-align: left;
    font-size: 13px;
  }
  th {
    font-weight: 500;
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 12px;
    white-space: nowrap;
  }
  th.sortable {
    cursor: pointer;
    user-select: none;
    &:hover { color: ${({ theme }) => theme.colors.primary}; }
  }
  th .sortInd {
    display: inline-block;
    margin-left: 6px;
    color: #C0C7D1;
    font-size: 10px;
    line-height: 1;
    vertical-align: middle;
  }
  th.active .sortInd { color: ${({ theme }) => theme.colors.primary}; }
  tbody tr {
    border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
    cursor: pointer;
    &:hover { background: #F9FAFB; }
  }
`;

const StatusText = styled.span`
  color: ${({ $c }) => $c};
  font-size: 13px;
  font-weight: 500;
`;

const CatText = styled.span`
  font-size: 12px;
  color: ${({ $color }) => $color || '#4A6CF7'};
  font-weight: 500;
`;

const RowActions = styled.div` display: flex; gap: 6px; `;
const MiniBtn = styled.button`
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  border: none;
  color: #fff;
  cursor: pointer;
  background: ${({ $variant }) => ($variant === 'danger' ? '#EF4444' : '#4A6CF7')};
  &:hover { opacity: 0.9; }
`;

// ─── 모달 ───
const Overlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000;
`;
const Modal = styled.div`
  position: relative;
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  width: 100%; max-width: 480px;
  max-height: 92vh; overflow-y: auto;
`;
const CloseIcon = styled.button`
  position: absolute; top: 14px; right: 14px;
  background: none; border: none; padding: 4px;
  cursor: pointer; color: #6B7280;
  display: inline-flex; align-items: center; justify-content: center;
  &:hover { color: #1F2937; }
`;
const ModalTitle = styled.h3` font-size: 16px; font-weight: 600; margin-bottom: 16px; `;
const Field = styled.div` margin-bottom: 12px; `;
const Label = styled.label`
  display: block; font-size: 12px; color: #6B7280;
  margin-bottom: 4px;
`;
const Input = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: 13px;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;
const Textarea = styled.textarea`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: 13px;
  resize: vertical; min-height: 60px;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;
const CatChips = styled.div` display: flex; flex-wrap: wrap; gap: 6px; `;
const Chip = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  border: 1px solid ${({ $on, theme }) => ($on ? theme.colors.primary : theme.colors.border)};
  background: ${({ $on, theme }) => ($on ? theme.colors.primary : '#fff')};
  color: ${({ $on }) => ($on ? '#fff' : '#374151')};
  cursor: pointer;
`;
const ModalFooter = styled.div`
  display: flex; justify-content: flex-end; gap: 8px;
  margin-top: 16px;
`;
const Note = styled.p`
  display: flex; gap: 8px; align-items: flex-start;
  margin: 12px 0 0;
  padding: 10px 12px;
  background: #FEF3C7;
  border: 1px solid #FCD34D;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.5;
  color: #92400E;

  &::before {
    content: '!';
    flex: 0 0 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px; height: 18px;
    background: #92400E;
    color: #fff;
    border-radius: 50%;
    font-weight: 700;
    font-size: 12px;
  }
`;

// ───── 헬퍼 ─────
const formatDate = (v) => {
  if (!v) return '-';
  if (typeof v === 'string') return v.slice(0, 16);
  try {
    const d = new Date(v);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return '-'; }
};

const partnerCats = (u) => {
  if (Array.isArray(u.service_categories)) return u.service_categories;
  if (u.category) return [u.category];
  return [];
};

const downloadCSV = (rows, filename) => {
  const csv = rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

// ───── 컴포넌트 ─────
const PartnerList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');

  useEffect(() => {
    const s = searchParams.get('status');
    if (s) setStatusFilter(s);
  }, [searchParams]);

  const [catFilter, setCatFilter] = useState('all');
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [showPayReq, setShowPayReq] = useState(false);

  const loadUsers = useCallback(async () => {
    setListLoading(true);
    try {
      const list = await getAllPartners();
      setUsers(list);
    } catch (err) { console.error(err); }
    finally { setListLoading(false); }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const filtered = useMemo(() => {
    let list = users.filter((u) => {
      if (statusFilter !== 'all' && u.status !== statusFilter) return false;
      if (catFilter !== 'all' && !partnerCats(u).includes(catFilter)) return false;
      if (search) {
        const q = search.toLowerCase();
        const name = (u.business_name || u.name || '').toLowerCase();
        const manager = (u.name || '').toLowerCase();
        const phone = (u.phone || '').toLowerCase();
        if (!name.includes(q) && !manager.includes(q) && !phone.includes(q)) return false;
      }
      return true;
    });
    const STATUS_ORDER = { pending: 0, active: 1, suspended: 2, inactive: 3 };
    const getVal = (u) => {
      if (sortKey === 'name') return (u.business_name || u.name || '').toLowerCase();
      if (sortKey === 'status') return STATUS_ORDER[u.status] ?? 99;
      return typeof u.createdAt === 'string' ? u.createdAt : '';
    };
    list.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [users, statusFilter, catFilter, sortKey, sortDir, search]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'createdAt' ? 'desc' : 'asc');
    }
    setPage(1);
  };

  const sortInd = (key) => {
    if (sortKey !== key) return <span className="sortInd">▲▼</span>;
    return <span className="sortInd">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  };

  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const handleApprove = async (e, uid) => {
    e.stopPropagation();
    if (!window.confirm('이 계정을 승인하시겠습니까?')) return;
    try { await approveUser(uid); alert('승인 완료'); loadUsers(); }
    catch (err) { alert(err.message || '승인 실패'); }
  };

  const handleReject = async (e, uid) => {
    e.stopPropagation();
    if (!window.confirm('이 계정을 거절하시겠습니까?')) return;
    try { await rejectUser(uid); alert('거절 처리되었습니다.'); loadUsers(); }
    catch (err) { alert(err.message || '거절 실패'); }
  };

  const handleExcel = () => {
    const header = ['업체명', '업종', '담당자', '연락처', '이메일', '주소', '상태', '등록일'];
    const rows = filtered.map((u) => [
      u.business_name || u.name || '',
      partnerCats(u).map((c) => SERVICE_CATEGORIES[c]?.label).filter(Boolean).join(', '),
      u.name || '',
      u.phone || '',
      u.email || '',
      u.address || '',
      STATUS_LABEL[u.status] || u.status,
      u.createdAt || '',
    ]);
    downloadCSV([header, ...rows], `제휴업체_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  return (
    <Wrap>
      <TopRow>
        <TitleBox>
          <Title>제휴업체 관리<CountText>총 {users.length}개 업체 등록</CountText></Title>
        </TitleBox>
        <ActionRow>
          <Btn onClick={() => setShowPayReq(true)}>결제 요청 생성</Btn>
          <Btn onClick={handleExcel}>엑셀 다운로드</Btn>
          <Btn $variant="primary" onClick={() => setShowCreate(true)}>신규 제휴업체 등록</Btn>
        </ActionRow>
      </TopRow>

      <FilterBar>
        <SearchInput
          placeholder="업체명 / 담당자 / 연락처 검색"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        <Select value={catFilter} onChange={(e) => { setCatFilter(e.target.value); setPage(1); }}>
          <option value="all">카테고리 전체</option>
          {Object.entries(SERVICE_CATEGORIES).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </Select>
        <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="all">상태 전체</option>
          <option value="active">활성</option>
          <option value="pending">승인 대기</option>
          <option value="suspended">정지</option>
          <option value="inactive">비활성</option>
        </Select>
      </FilterBar>

      <MobileCardList aria-label="제휴업체 카드 목록">
        {paged.map((u) => {
          const cats = partnerCats(u);
          const detailPath = `/admin/members/partners/${u.uid}`;
          return (
            <MobileListCard
              key={u.uid}
              $clickable
              role="button"
              tabIndex={0}
              onClick={() => navigate(detailPath)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  navigate(detailPath);
                }
              }}
            >
              <MobileCardHeader>
                <MobileCardHeading>
                  <MobileCardTitle>{u.business_name || u.name || '-'}</MobileCardTitle>
                  <MobileCardSubtitle>{cats.map((c) => SERVICE_CATEGORIES[c]?.label || c).join(' · ') || '등록 업종 없음'}</MobileCardSubtitle>
                </MobileCardHeading>
                <MobileCardStatus><StatusText $c={STATUS_COLOR[u.status] || '#9CA3AF'}>{STATUS_LABEL[u.status] || u.status}</StatusText></MobileCardStatus>
              </MobileCardHeader>
              <MobileCardGrid>
                <MobileCardField><MobileCardLabel>연락처</MobileCardLabel><MobileCardValue>{u.phone || u.manager_phone || '-'}</MobileCardValue></MobileCardField>
                <MobileCardField><MobileCardLabel>등록일</MobileCardLabel><MobileCardValue>{formatDate(u.createdAt)}</MobileCardValue></MobileCardField>
                <MobileCardField $wide><MobileCardLabel>주소</MobileCardLabel><MobileCardValue>{u.address || '-'}</MobileCardValue></MobileCardField>
              </MobileCardGrid>
              <MobileCardActions onClick={(event) => event.stopPropagation()}>
                {u.status === 'pending' ? (
                  <>
                    <MiniBtn onClick={(event) => handleApprove(event, u.uid)}>승인</MiniBtn>
                    <MiniBtn $variant="danger" onClick={(event) => handleReject(event, u.uid)}>거절</MiniBtn>
                  </>
                ) : (
                  <MiniBtn onClick={() => navigate(detailPath)}>상세 보기</MiniBtn>
                )}
              </MobileCardActions>
            </MobileListCard>
          );
        })}
        {listLoading ? <MobileListState>로딩 중...</MobileListState> : paged.length === 0 && <MobileListState>해당 조건의 업체가 없습니다.</MobileListState>}
      </MobileCardList>

      <DesktopListOnly>
      <Table>
        <thead>
          <tr>
            <th
              className={`sortable${sortKey === 'name' ? ' active' : ''}`}
              onClick={() => toggleSort('name')}
            >업체명{sortInd('name')}</th>
            <th>업종</th>
            <th>연락처</th>
            <th>주소</th>
            <th
              className={`sortable${sortKey === 'createdAt' ? ' active' : ''}`}
              onClick={() => toggleSort('createdAt')}
            >등록일{sortInd('createdAt')}</th>
            <th
              className={`sortable${sortKey === 'status' ? ' active' : ''}`}
              onClick={() => toggleSort('status')}
            >상태{sortInd('status')}</th>
            <th>관리</th>
          </tr>
        </thead>
        <tbody>
          {paged.map((u) => {
            const cats = partnerCats(u);
            return (
              <tr key={u.uid} onClick={() => navigate(`/admin/members/partners/${u.uid}`)}>
                <td style={{ fontWeight: 500 }}>{u.business_name || u.name || '-'}</td>
                <td>
                  {cats.length === 0 ? '-' : cats.map((c, i) => (
                    <span key={c}>
                      <CatText $color={SERVICE_CATEGORIES[c]?.color}>{SERVICE_CATEGORIES[c]?.label || c}</CatText>
                      {i < cats.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </td>
                <td>{u.phone || u.manager_phone || '-'}</td>
                <td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {u.address || '-'}
                </td>
                <td style={{ color: '#6B7280' }}>{formatDate(u.createdAt)}</td>
                <td><StatusText $c={STATUS_COLOR[u.status] || '#9CA3AF'}>{STATUS_LABEL[u.status] || u.status}</StatusText></td>
                <td onClick={(e) => e.stopPropagation()}>
                  <RowActions>
                    {u.status === 'pending' ? (
                      <>
                        <MiniBtn onClick={(e) => handleApprove(e, u.uid)}>승인</MiniBtn>
                        <MiniBtn $variant="danger" onClick={(e) => handleReject(e, u.uid)}>거절</MiniBtn>
                      </>
                    ) : (
                      <MiniBtn onClick={(e) => { e.stopPropagation(); navigate(`/admin/members/partners/${u.uid}`); }}>보기</MiniBtn>
                    )}
                  </RowActions>
                </td>
              </tr>
            );
          })}
          {listLoading ? (
            <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40 }}>로딩 중...</td></tr>
          ) : paged.length === 0 && (
            <tr><td colSpan={7} style={{ textAlign: 'center', color: '#9CA3AF', padding: 32 }}>해당 조건의 업체가 없습니다.</td></tr>
          )}
        </tbody>
      </Table>
      </DesktopListOnly>

      <Pagination current={page} total={Math.max(1, Math.ceil(filtered.length / PER_PAGE))} onChange={setPage} />

      {showCreate && (
        <CreatePartnerModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); loadUsers(); }}
        />
      )}
      {showPayReq && (
        <PayRequestModal
          partners={users.filter((u) => u.status === 'active')}
          onClose={() => setShowPayReq(false)}
        />
      )}
    </Wrap>
  );
};

// ───── 신규 등록 모달 ─────
const CreatePartnerModal = ({ onClose, onCreated }) => {
  const [f, setF] = useState({
    email: '', password: '', businessName: '', managerName: '', phone: '', address: '',
  });
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const toggleCat = (c) => setCats((p) => p.includes(c) ? p.filter((x) => x !== c) : [...p, c]);

  const submit = async () => {
    if (!f.email || !f.password || !f.businessName || !f.managerName || !f.phone) {
      alert('필수 항목을 입력하세요.');
      return;
    }
    if (cats.length === 0) {
      alert('제공 서비스를 하나 이상 선택하세요.');
      return;
    }
    setLoading(true);
    try {
      await signup({
        email: f.email,
        password: f.password,
        name: f.managerName,
        phone: f.phone,
        businessName: f.businessName,
        role: 'partner',
        tempPassword: true,
        profile: {
          business_name: f.businessName,
          address: f.address,
          service_categories: cats,
          manager_phone: f.phone,
        },
      });
      alert('등록 완료. 활성화 처리 후 업체에 임시 비밀번호를 안내해 주세요.');
      onCreated();
    } catch (err) {
      alert(err.message || '등록 실패');
    } finally { setLoading(false); }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <CloseIcon onClick={onClose} aria-label="닫기"><IoClose size={20} /></CloseIcon>
        <ModalTitle>신규 제휴업체 등록</ModalTitle>
        <Field><Label>이메일 (로그인 ID) *</Label><Input value={f.email} onChange={(e) => set('email', e.target.value)} /></Field>
        <Field><Label>비밀번호 *</Label><Input type="password" value={f.password} onChange={(e) => set('password', e.target.value)} /></Field>
        <Field><Label>업체명 *</Label><Input value={f.businessName} onChange={(e) => set('businessName', e.target.value)} /></Field>
        <Field><Label>담당자명 *</Label><Input value={f.managerName} onChange={(e) => set('managerName', e.target.value)} /></Field>
        <Field><Label>연락처 *</Label><Input value={f.phone} onChange={(e) => set('phone', e.target.value)} placeholder="010-0000-0000" /></Field>
        <Field><Label>주소</Label><Input value={f.address} onChange={(e) => set('address', e.target.value)} /></Field>
        <Field>
          <Label>제공 서비스 (복수 선택) *</Label>
          <CatChips>
            {PARTNER_SIGNUP_CATEGORIES.map((c) => (
              <Chip key={c.id} $on={cats.includes(c.id)} onClick={() => toggleCat(c.id)}>{c.label}</Chip>
            ))}
          </CatChips>
        </Field>
        <Note>※ 등록 후 현재 로그인이 자동 해제될 수 있습니다. 다시 로그인해 주세요.</Note>
        <ModalFooter>
          <Btn onClick={onClose} disabled={loading}>취소</Btn>
          <Btn $variant="primary" onClick={submit} disabled={loading}>{loading ? '등록 중...' : '등록'}</Btn>
        </ModalFooter>
      </Modal>
    </Overlay>
  );
};

// ───── 결제 요청 생성 모달 ─────
const PayRequestModal = ({ partners, onClose }) => {
  const [partnerId, setPartnerId] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('bank');
  const [memo, setMemo] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!partnerId) return alert('업체를 선택하세요.');
    const amt = Number(String(amount).replace(/[^0-9]/g, ''));
    if (!amt || amt <= 0) return alert('청구 금액을 입력하세요.');
    setLoading(true);
    try {
      await addDoc(collection(db, 'partner_payment_requests'), {
        partner_id: partnerId,
        amount: amt,
        method,
        status: 'pending',
        label: memo || '본사 청구',
        memo: memo || '',
        created_at: serverTimestamp(),
      });
      alert('결제 요청이 생성되었습니다.');
      onClose();
    } catch (err) {
      alert(err.message || '생성 실패');
    } finally { setLoading(false); }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <CloseIcon onClick={onClose} aria-label="닫기"><IoClose size={20} /></CloseIcon>
        <ModalTitle>결제 요청 생성</ModalTitle>
        <Field>
          <Label>업체 선택 *</Label>
          <Select value={partnerId} onChange={(e) => setPartnerId(e.target.value)} style={{ width: '100%' }}>
            <option value="">선택하세요</option>
            {partners.map((p) => (
              <option key={p.uid} value={p.uid}>{p.business_name || p.name}</option>
            ))}
          </Select>
        </Field>
        <Field>
          <Label>청구 금액 *</Label>
          <Input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="예: 100000"
          />
        </Field>
        <Field>
          <Label>결제 수단 *</Label>
          <Select value={method} onChange={(e) => setMethod(e.target.value)} style={{ width: '100%' }}>
            <option value="bank">계좌이체</option>
            <option value="card">카드</option>
          </Select>
        </Field>
        <Field>
          <Label>메모 (표시 라벨)</Label>
          <Textarea value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="예: 4월 마일리지 충전" />
        </Field>
        <ModalFooter>
          <Btn onClick={onClose} disabled={loading}>취소</Btn>
          <Btn $variant="primary" onClick={submit} disabled={loading}>{loading ? '생성 중...' : '요청 생성'}</Btn>
        </ModalFooter>
      </Modal>
    </Overlay>
  );
};

export default PartnerList;
